import { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Select,
  MenuItem,
  Fade,
  Snackbar,
} from "@mui/material";
import {
  Close,
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
  CheckCircleOutline,
} from "@mui/icons-material";
import { colors } from "./Followup.styles";

const DAYS_OF_WEEK = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const rescheduleReasons = [
  "Client Busy",
  "Client Unavailable",
  "Wrong Time",
  "Need More Information",
  "Technical Issues",
  "Client Request",
  "Other",
];

const timeOptions = [
  "9:00 AM",
  "9:30 AM",
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "1:00 PM",
  "1:30 PM",
  "2:00 PM",
  "2:30 PM",
  "3:00 PM",
  "3:30 PM",
  "4:00 PM",
  "4:30 PM",
  "5:00 PM",
  "5:30 PM",
];

// const durationOptions = [
//   'Dur. 15 min', 'Dur. 30 min', 'Dur. 45 min', 'Dur. 1 hour', 'Dur. 1.5 hours', 'Dur. 2 hours',
// ];

const RescheduleModal = ({ open, onClose, task, onSubmit }) => {
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(today);
  const [reason, setReason] = useState("Client Busy");
  const [priority, setPriority] = useState("medium");
  const [notes, setNotes] = useState("");
  const [preferredTime, setPreferredTime] = useState("11:30 AM");
  const [duration, setDuration] = useState("Dur. 30 min");
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [scheduledInfo, setScheduledInfo] = useState({ date: "", time: "" });

  // Initialize state from task data when modal opens
  useEffect(() => {
    if (open && task) {
      console.log("RescheduleModal - task data:", task);

      // Get the due date - try multiple possible field names
      const dueDate = task.dueDate || task.date || task.scheduledDate || task.scheduled_date;
      if (dueDate) {
        const existingDate = new Date(dueDate);

        // Create a normalized UTC date for consistent comparison
        const normalizedDate = new Date(Date.UTC(
          existingDate.getFullYear(),
          existingDate.getMonth(),
          existingDate.getDate()
        ));

        // Set the selected date
        setSelectedDate(normalizedDate);

        // Set calendar to the existing date's month
        setCurrentMonth(existingDate.getMonth());
        setCurrentYear(existingDate.getFullYear());
      }

      // Set preferred time from task if available, otherwise format from dueDate
      const preferredTimeValue = task.preferredTime || task.preferred_time;
      if (preferredTimeValue) {
        setPreferredTime(preferredTimeValue);
      } else if (dueDate) {
        const existingDate = new Date(dueDate);
        const hours = existingDate.getHours();
        const minutes = existingDate.getMinutes();
        const timeString = `${hours > 12 ? hours - 12 : hours === 0 ? 12 : hours}:${minutes.toString().padStart(2, "0")} ${hours >= 12 ? "PM" : "AM"}`;
        setPreferredTime(timeString);
      }

      // Set priority from task if available (handle both uppercase and lowercase)
      const priorityValue = task.priority || task.priorityLevel;
      if (priorityValue) {
        const priorityLower = priorityValue.toString().toLowerCase();
        // Map "med" to "medium" for consistency
        const normalizedPriority = priorityLower === "med" ? "medium" : priorityLower;
        if (["low", "medium", "high"].includes(normalizedPriority)) {
          setPriority(normalizedPriority);
        }
      }

      // Set notes from task if available - try multiple field names
      const notesValue = task.notes || task.note || task.description || task.comments || task.context;
      if (notesValue) {
        setNotes(notesValue);
      } else {
        setNotes("");
      }

      // Set reason from task if available - try multiple field names
      const reasonValue = task.reason || task.rescheduleReason || task.reschedule_reason || task.statusReason;
      if (reasonValue && rescheduleReasons.includes(reasonValue)) {
        setReason(reasonValue);
      } else {
        setReason("Client Busy");
      }
    }
  }, [open, task]);

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, disabled: true });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(Date.UTC(currentYear, currentMonth, day));
      const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));

      // Helper to compare dates by UTC components
      const isSameDate = (d1, d2) => {
        return d1.getUTCFullYear() === d2.getUTCFullYear() &&
               d1.getUTCMonth() === d2.getUTCMonth() &&
               d1.getUTCDate() === d2.getUTCDate();
      };

      days.push({
        day,
        date,
        isToday: isSameDate(date, todayUTC),
        isSelected: selectedDate && isSameDate(date, selectedDate),
        disabled: date < todayUTC,
      });
    }

    return days;
  }, [currentMonth, currentYear, selectedDate]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDayClick = (dayInfo) => {
    if (dayInfo.disabled || !dayInfo.date) return;
    setSelectedDate(dayInfo.date);
  };

  const formatScheduledDate = (date) => {
    const options = { month: "long", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  const handleSubmit = () => {
    const rescheduleData = {
      taskId: task?.id,
      newDate: selectedDate,
      rescheduleReason: reason,
      priority,
      notes,
      preferredTime,
      duration,
    };

    // Set scheduled info for snackbar
    setScheduledInfo({
      date: formatScheduledDate(selectedDate),
      time: preferredTime,
    });

    onSubmit(rescheduleData);
    setSnackbarOpen(true);
    handleClose();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClose = () => {
    setSelectedDate(today);
    setReason("Client Busy");
    setPriority("medium");
    setNotes("");
    setPreferredTime("11:30 AM");
    setDuration("Dur. 30 min");
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    onClose();
  };

  const selectStyles = {
    borderRadius: "8px",
    fontSize: "14px",
    bgcolor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E8F0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#CBD5E1",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.primary,
    },
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "14px",
      bgcolor: "#fff",
      "& fieldset": {
        borderColor: "#E2E8F0",
      },
      "&:hover fieldset": {
        borderColor: "#CBD5E1",
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.primary,
      },
    },
  };

  return (
    <>
      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: colors.primary,
            color: "#fff",
            px: 2.5,
            py: 1.5,
            borderRadius: "10px",
            boxShadow: "0 4px 20px rgba(124,58,237,0.4)",
            minWidth: 300,
          }}
        >
          <CheckCircleOutline sx={{ fontSize: 22, color: "#fff" }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#fff" }}
            >
              Follow-up successfully rescheduled
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}
            >
              Scheduled for {scheduledInfo.date}, {scheduledInfo.time}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleSnackbarClose}
            sx={{
              color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Snackbar>

      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        }}
      >
        <Fade in={open}>
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              borderRadius: "16px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              outline: "none",
              width: 680,
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 3,
                borderBottom: "1px solid #E2E8F0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    bgcolor: (theme) => `${theme.palette.primary.main}1A`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CalendarMonth sx={{ fontSize: 20, color: "primary.main" }} />
                </Box>
                <Box>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "20px", color: "#111827" }}
                  >
                    Reschedule Follow-up
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                    {task?.clientName || task?.client || "Jane Doe"} •{" "}
                    {task?.taskType || task?.task || "Share Proposal"}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleClose}
                sx={{
                  color: "#9CA3AF",
                  "&:hover": { bgcolor: "#F3F4F6", color: "#6B7280" },
                }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ display: "flex", p: 3 }}>
              {/* Left Side - Calendar */}
              <Box sx={{ flex: 1, pr: 3 }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#6B7280",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    mb: 1.5,
                  }}
                >
                  Select New Date
                </Typography>

                {/* Calendar */}
                <Box
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "12px",
                    p: 2,
                    bgcolor: "#fff",
                  }}
                >
                  {/* Calendar Header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <IconButton
                      onClick={handlePrevMonth}
                      sx={{ width: 28, height: 28, color: "#9CA3AF" }}
                    >
                      <ChevronLeft fontSize="small" />
                    </IconButton>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "15px",
                        color: "#111827",
                      }}
                    >
                      {MONTHS[currentMonth]} {currentYear}
                    </Typography>
                    <IconButton
                      onClick={handleNextMonth}
                      sx={{ width: 28, height: 28, color: "#9CA3AF" }}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Week Days */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    {DAYS_OF_WEEK.map((day, index) => (
                      <Typography
                        key={index}
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#9CA3AF",
                          textAlign: "center",
                          py: 1,
                        }}
                      >
                        {day}
                      </Typography>
                    ))}
                  </Box>

                  {/* Days Grid */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      gap: 0.5,
                    }}
                  >
                    {calendarDays.map((dayInfo, index) => (
                      <Box
                        key={index}
                        onClick={() => handleDayClick(dayInfo)}
                        sx={{
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "8px",
                          fontSize: "13px",
                          fontWeight: dayInfo.isSelected ? 600 : 400,
                          color: dayInfo.disabled
                            ? "#CBD5E1"
                            : dayInfo.isSelected
                              ? "#fff"
                              : "#374151",
                          bgcolor: dayInfo.isSelected
                            ? "primary.main"
                            : dayInfo.isToday
                              ? "#F3E8FF"
                              : "transparent",
                          cursor: dayInfo.disabled ? "default" : "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: dayInfo.disabled
                              ? "transparent"
                              : dayInfo.isSelected
                                ? "primary.main"
                                : "#F1F5F9",
                          },
                        }}
                      >
                        {dayInfo.day}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Time Preference */}
                <Box sx={{ mt: 3 }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6B7280",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      mb: 1.5,
                    }}
                  >
                    Time Preference
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Select
                      value={preferredTime}
                      onChange={(e) => setPreferredTime(e.target.value)}
                      size="small"
                      sx={{ ...selectStyles, flex: 1 }}
                    >
                      {timeOptions.map((time) => (
                        <MenuItem key={time} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                    {/* <Select
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    size="small"
                    sx={{ ...selectStyles, flex: 1 }}
                  >
                    {durationOptions.map((dur) => (
                      <MenuItem key={dur} value={dur}>{dur}</MenuItem>
                    ))}
                  </Select> */}
                  </Box>
                </Box>
              </Box>

              {/* Right Side - Reason & Priority */}
              <Box sx={{ flex: 1, pl: 3, borderLeft: "1px solid #E2E8F0" }}>
                {/* Reason for Rescheduling */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6B7280",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      mb: 1.5,
                    }}
                  >
                    Reason for Rescheduling
                  </Typography>
                  <Select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    fullWidth
                    size="small"
                    sx={selectStyles}
                  >
                    {rescheduleReasons.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Priority Level */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6B7280",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      mb: 1.5,
                    }}
                  >
                    Priority Level
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      overflow: "hidden",
                      bgcolor: "#fff",
                    }}
                  >
                    {["low", "medium", "high"].map((level) => (
                      <Box
                        key={level}
                        component="button"
                        onClick={() => setPriority(level)}
                        sx={{
                          flex: 1,
                          py: 1,
                          px: 1.5,
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color:
                            priority === level
                              ? level === "high"
                                ? "#DC2626"
                                : level === "medium"
                                  ? "#D97706"
                                  : "#374151"
                              : "#9CA3AF",
                          bgcolor:
                            priority === level
                              ? level === "high"
                                ? "#FEE2E2"
                                : level === "medium"
                                  ? "#FEF3C7"
                                  : "#F1F5F9"
                              : "transparent",
                          border: "none",
                          borderRight:
                            level !== "high" ? "1px solid #E2E8F0" : "none",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: priority === level ? undefined : "#F8FAFC",
                          },
                        }}
                      >
                        {level.toUpperCase()}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Context & Notes */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6B7280",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      mb: 1.5,
                    }}
                  >
                    Context & Notes
                  </Typography>
                  <TextField
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add details about why this was rescheduled..."
                    fullWidth
                    multiline
                    rows={5}
                    size="small"
                    sx={inputStyles}
                  />
                </Box>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                p: 3,
                borderTop: "1px solid #E2E8F0",
                bgcolor: "#F8FAFC",
              }}
            >
              <Button
                onClick={handleClose}
                sx={{
                  textTransform: "none",
                  color: "#6B7280",
                  fontWeight: 500,
                  px: 3,
                  "&:hover": { bgcolor: "#F3F4F6" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  textTransform: "none",
                  bgcolor: "primary.main",
                  fontWeight: 600,
                  px: 4,
                  py: 1.25,
                  borderRadius: "8px",
                  boxShadow: (theme) =>
                    `0 4px 12px ${theme.palette.primary.main}4D`,
                  "&:hover": {
                    bgcolor: "primary.dark",
                    boxShadow: (theme) =>
                      `0 6px 16px ${theme.palette.primary.main}66`,
                  },
                }}
              >
                Confirm Reschedule
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default RescheduleModal;
